import React, { useContext, useState } from "react";
import { useEffect } from "react";
import QuoteResultsContainer from "../../../../containers/QuoteResultsContainer/QuoteResultsContainer";
import { IQuoteCardResult } from "../../../../interfaces/results/IQuoteCardResult";
import useSorter from "../../../../hooks/useSorter";
import ProductPageQuotesHeader from "../ProductPageQuotesHeader/ProductPageQuotesHeader";
import { QuoteGroupContext } from "../../../../providers/RootContextProvider";
import { IClient } from "../../../../../common/interfaces/IClient";
import cx from "classnames";
import useDataLayer from "../../../../hooks/useDataLayer";
import styles from "./ProductPageQuotes.module.scss";
import { SplitTreatments } from "@splitsoftware/splitio-react";
import { FeatureFlag } from "../../../../enums/FeatureFlags";
import SortBy from "../../../../enums/SortBy";
import QuoteControls from "../../../../components/QuoteControls/QuoteControls";
import QuoteErrorType from "../../../../enums/QuoteErrorType";
import useIsMounted from "../../../../../common/hooks/useIsMounted";
import DapWarningDialog from "../../../../../digital-advice/components/Modals/DapWarningDialog";
import { useHistory } from "react-router-dom";

interface IProductPageQuotesProps {
    className?: string;
    groupId: string;
    clients: IClient[];
    cards: IQuoteCardResult[];
    isDisabled?: boolean;
}

const ProductPageQuotes: React.FC<IProductPageQuotesProps> = (props) => {
    const { className, groupId, isDisabled } = props;

    const { sortQuoteResult } = useSorter();
    const { actions, selectors } = useContext(QuoteGroupContext);
    const search = new URLSearchParams(location.search);
    const { sendPageView } = useDataLayer();

    const isLoading = selectors.isLoadingQuoteResult();
    const history = useHistory();
    const isMounted = useIsMounted();

    // State
    const [cards, setResults] = useState<IQuoteCardResult[]>(props.cards);
    const [sortBy, setSortBy] = useState<SortBy>(
        cards[0]?.result.settings[0]?.sortBy || SortBy.LowestPrice
    );
    const [isFromDAP, setIsFromDAP] = useState(false);
    const [isOpenWarningDialogModal, setIsOpenWarningDialogModal] = useState(false);
    const [warningNextStepUrl, setWarningNextStepUrl] = useState("");

    const handleWarningDialogModal = () => {
        const isQuoteWarningConfirm =
            !!window.localStorage.getItem("quote-modified-warning") || false;
        if (!isQuoteWarningConfirm) {
            setIsOpenWarningDialogModal(true);
        } else {
            history.push(warningNextStepUrl);
        }
    };

    // Effects
    useEffect(() => {
        setResults(sortQuoteResult(props.cards, sortBy, selectors.getGroupId()));
    }, [sortBy, props.cards]);

    useEffect(() => {
        setIsFromDAP(search.has("da"));
        sendPageView("/compare/results");
    }, []);

    useEffect(() => {
        if (!isLoading && cards.length > 0 && selectors.hasContactDetails()) {
            cards[0].result.settings.forEach((setting) => {
                sendPageView(`/compare/${setting.productType}`);
                sendPageView(`/compare/view-results/${setting.productType}`);
            });
        }
    }, [isLoading, cards]);

    const showMinimumPremiumAlert =
        !isLoading &&
        cards.some((c) =>
            c.result.errors.some((e) => e.type === QuoteErrorType.MinimumPremium)
        );

    const showPreExistingAlert =
        search.has("da") &&
        search.has("hasHealthInsurance") &&
        search.get("hasHealthInsurance") === "true";

    return (
        <div
            className={cx(styles["container"], {
                [className]: !!className,

                ["fade-light"]: isDisabled && !isLoading
            })}>
            <div
                className={cx(styles["disable-controls"], {
                    ["layout-hidden"]: !isDisabled
                })}
            />

            <DapWarningDialog
                isOpen={isOpenWarningDialogModal}
                onClose={(isContinue: boolean) => {
                    if (isContinue) history.push(warningNextStepUrl);
                    isMounted.current && setIsOpenWarningDialogModal(false);
                }}
            />
            <SplitTreatments names={[FeatureFlag.QuoteTableHeader]}>
                {({ treatments }) => {
                    if (
                        window.splitioFactory &&
                        treatments[FeatureFlag.QuoteTableHeader].treatment !== "on"
                    ) {
                        return (
                            <ProductPageQuotesHeader
                                className="gap-4xl gap-pre-xl"
                                productTypes={cards[0]?.result.settings.map(
                                    (s) => s.productType
                                )}
                                numberOfPolicies={cards.length}
                                isHidden={isLoading || cards.length === 0}
                                sortBy={sortBy}
                                showMinimumPremiumAlert={showMinimumPremiumAlert}
                                showPreExistingAlert={showPreExistingAlert}
                                handleChangeSortBy={setSortBy}
                                currentView={selectors.getCurrentTableView()}
                                setCurrentView={actions.setCurrentTableView}
                                isDisabled={isDisabled}>
                                <QuoteControls
                                    isDisabled={isDisabled}
                                    setEditIcon="EditCogPencil"
                                    isFromDAP={isFromDAP}
                                    showWarning={handleWarningDialogModal}
                                    setWarningNextStepUrl={setWarningNextStepUrl}
                                />
                            </ProductPageQuotesHeader>
                        );
                    }

                    return (
                        <ProductPageQuotesHeader
                            className="gap-2xl gap-pre-3xl"
                            productTypes={cards[0]?.result.settings.map(
                                (s) => s.productType
                            )}
                            numberOfPolicies={cards.length}
                            isHidden={isLoading || cards.length === 0}
                            sortBy={sortBy}
                            showMinimumPremiumAlert={showMinimumPremiumAlert}
                            showPreExistingAlert={showPreExistingAlert}
                            handleChangeSortBy={setSortBy}
                            currentView={selectors.getCurrentTableView()}
                            setCurrentView={actions.setCurrentTableView}
                            isDisabled={isDisabled}
                            switcherAlignment="start"
                            dropdownInlineLabel={
                                <React.Fragment>
                                    <span className={cx("layout-hidden-mobile")}>
                                        Sort by:
                                    </span>
                                    <span className={cx("layout-hidden-desktop")}>
                                        Sort:
                                    </span>
                                </React.Fragment>
                            }>
                            <QuoteControls
                                isDisabled={isDisabled}
                                setEditIcon="EditCogPencil"
                                isFromDAP={isFromDAP}
                                showWarning={handleWarningDialogModal}
                                setWarningNextStepUrl={setWarningNextStepUrl}
                            />
                        </ProductPageQuotesHeader>
                    );
                }}
            </SplitTreatments>

            <QuoteResultsContainer
                cards={cards}
                sortBy={sortBy}
                caseId={0}
                groupId={groupId}
                isLoading={isLoading}
                isDisabled={isDisabled}
            />
        </div>
    );
};

export default ProductPageQuotes;
